import {
  KalturaAssetMetaOrTagGroupBy,
  KalturaFilterPager,
  KalturaLiveAsset,
} from '@kaltura-ott/ott-client-types/api/types';
import {
  AssetGetAction,
  KalturaAsset,
  KalturaAssetReferenceType,
  KalturaSearchAssetFilter,
} from '@kaltura-ott/ott-client-types/api/types';
import { Observable, of, throwError, forkJoin, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { KuxLinearType, KuxLogo, KuxMediaAssetType, KuxProgramType, KuxSeriesType } from '../interfaces';

import { getAssetList, getAssetsByIdList, getAssetsRecursively } from './asset';
import { assetsToType, assetToType } from './asset-v2.helper';
import { addKsql } from './category.tree.helper';
import { KuXDms, MediaType } from './dms';
import { getLinearCacheList, setLinearCache } from './epg-cache';
import { kalturaClient } from './kaltura-clent';

export const addChannelStats = <T extends { id: number; watchedCount: number }>(
  asset: T,
  channelsStats: Map<number, number>,
): (T & { watchedCount: number }) | void => {
  const channelStat = channelsStats.get(asset.id);

  asset['watchedCount'] = channelStat ?? 0;

  return asset;
};

export const getMediaAssetsByIdList = (
  idList: number[],
  kSql?: string,
  pager?: KalturaFilterPager,
  groupBy?: string,
  isGroupedGrid?: boolean,
): Observable<(KuxMediaAssetType | undefined)[]> => {
  if (!idList.length) {
    return of([]);
  }

  return from(getLinearCacheList(idList.map((id) => ({ id })))).pipe(
    switchMap((cachedLinears: KuxLinearType[]) => {
      const cachedIds = cachedLinears.map((linear) => linear.id);
      const notCachedLinearIds = idList.filter((id) => !cachedIds.includes(id));
      if (!notCachedLinearIds.length) {
        return of(cachedLinears);
      }

      const filter = new KalturaSearchAssetFilter({
        kSql: addKsql(kSql, `media_id:'${notCachedLinearIds.join(',')}'`),
        ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
      });

      return getAssetList({ filter, pager }).pipe(
        map((assetList) => {
          const assets = assetList.objects;
          const items = assets?.map((asset) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const typedAsset = assetToType<KalturaAsset, any>(asset, { isGroupedGrid });
            if (asset instanceof KalturaLiveAsset) {
              setLinearCache({ id: asset.id }, typedAsset);
            }
            return typedAsset;
          });
          return [...cachedLinears, ...(items || [])];
        }),
      );
    }),
  );
};

export const getMediaAsset = <T extends KuxMediaAssetType>(
  id: number,
  assetReferenceType = KalturaAssetReferenceType.media,
): Observable<T | undefined> => {
  const action = new AssetGetAction({
    id: id.toString(),
    assetReferenceType,
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    map((asset) => assetToType<KalturaAsset, T>(asset)),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getSeriesAsset = (seriesId: string): Observable<KuxSeriesType | undefined> => {
  return getAssetList({
    filter: new KalturaSearchAssetFilter({
      kSql: `(and SeriesID = '${seriesId}' asset_type = '${KuXDms.mediaTypes[MediaType.Series]}')`,
    }),
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize: 1,
    }),
  }).pipe(
    map((res) => assetToType<KalturaAsset, KuxSeriesType>(res.objects[0])),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getLinearById = (ids: number[]) => {
  return forkJoin({
    assets: getAssetsByIdList(ids),
  }).pipe(
    map(({ assets }) => {
      const typedAssets = assetsToType<KalturaAsset, KuxLinearType>(assets.objects);

      return typedAssets;
    }),
  );
};

export const getMosaicProgramAssetByExternalId = (externalId: string): Observable<KuxProgramType | undefined> => {
  return getAssetList({
    filter: new KalturaSearchAssetFilter({
      kSql: `(and IsMosaicEvent='1' MosaicInfo='mosaic' externalId='${externalId}')`,
    }),
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize: 1,
    }),
  }).pipe(
    map((res) => assetToType<KalturaAsset, KuxProgramType>(res.objects[0])),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getAssetsByExternalIds = <T extends KuxMediaAssetType>(
  externalIds: string[],
): Observable<(T | undefined)[] | undefined> => {
  return getAssetsRecursively({
    filter: new KalturaSearchAssetFilter({
      kSql: `(or ${externalIds.map((it) => `externalId='${it}'`).join(' ')})`,
    }),
  }).pipe(
    map((res) => assetsToType<KalturaAsset, T>(res.objects)),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getAssetByIdOrExternalId = <T extends KuxMediaAssetType>({
  id,
  assetReferenceType = KalturaAssetReferenceType.media,
  externalId,
}: {
  id?: number;
  assetReferenceType?: KalturaAssetReferenceType;
  externalId?: string;
}): Observable<T | undefined> => {
  if (id && assetReferenceType) {
    return getMediaAsset<T>(id, assetReferenceType);
  } else if (externalId) {
    return getAssetsByExternalIds<T>([externalId]).pipe(map((assets) => assets?.[0])) as Observable<T>;
  }

  return of(undefined);
};

export const getLogoAsset = (externalProvider = 'appMainLogo'): Observable<KuxLogo | undefined> => {
  if (!KuXDms.mediaTypes[MediaType.Logo]) {
    return of(undefined);
  }

  return getAssetList({
    filter: new KalturaSearchAssetFilter({
      kSql: `(and asset_type='${KuXDms.mediaTypes[MediaType.Logo]}' ExternalProvider='${externalProvider}')`,
    }),
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize: 1,
    }),
  }).pipe(
    map((res) => {
      return res.objects[0] && assetToType<KalturaAsset, KuxLogo>(res.objects[0]);
    }),
    catchError((error) => {
      return throwError(error);
    }),
  );
};
